import React, { useRef, useEffect, useCallback } from "react";
import "../../styles/HearFromOurClients.css";

import imgChinedu   from "../../assets/chinedu-okafor.png";
import imgAisha     from "../../assets/aisha-bello.png";
import imgRohit     from "../../assets/rohit-sangi.png";
import imgSandeep   from "../../assets/sandeep-rao.png";
import imgPooja     from "../../assets/pooja-shah.png";
import imgNeha      from "../../assets/neha-verma.png";
import imgSneha     from "../../assets/sneha-iyer.png";
import imgVikram    from "../../assets/vikram-singh.png";
import imgMichael   from "../../assets/michael-carter.png";
import imgEthan     from "../../assets/ethan-walker.png";
import imgPutri     from "../../assets/putri-maheswari.png";
import imgDewi      from "../../assets/dewi-lestari.png";
import imgKwame     from "../../assets/kwame-mensah.png";

const DEFAULT_TESTIMONIALS = [
  // Nigeria
  {
    name: "Chinedu Okafor",
    role: "cafe owner",
    avatar: imgChinedu,
    quote: "managing everything earlier was a bit stressful at times. now it feels more in control and less chaotic during peak hours.",
    badgeColor: "#F5CBA7",
  },
  {
    name: "Aisha Bello",
    role: "salon owner",
    avatar: imgAisha,
    quote: "didn't expect much at first, but it's been helpful in daily work. things just run a bit smoother now.",
    badgeColor: "#F4C2C2",
  },
  // India
  {
    name: "Rohit Sangi",
    role: "salon owner",
    avatar: imgRohit,
    quote: "earlier things used to get a bit unorganised during busy hours. now it feels more structured and easier to handle overall.",
    badgeColor: "#F4C2C2",
  },
  {
    name: "Sandeep Rao",
    role: "restaurant owner",
    avatar: imgSandeep,
    quote: "managing everything earlier was a bit stressful at times. now it feels more in control and less chaotic during peak hours.",
    badgeColor: "#CDB7F6",
  },
  {
    name: "Pooja Shah",
    role: "boutique owner",
    avatar: imgPooja,
    quote: "it's simple to use and doesn't take much time to figure out. i can manage things better now without depending on others.",
    badgeColor: "#F5CBA7",
  },
  {
    name: "Neha Verma",
    role: "cafe owner",
    avatar: imgNeha,
    quote: "didn't expect much at first, but it's been helpful in day-to-day work. it just makes things a bit smoother without extra effort.",
    badgeColor: "#B3E5FC",
  },
  {
    name: "Sneha Iyer",
    role: "home bakery",
    avatar: imgSneha,
    quote: "i like that it's not complicated. just helps keep things in order without adding extra work.",
    badgeColor: "#CDB7F6",
  },
  {
    name: "Vikram Singh",
    role: "gym owner",
    avatar: imgVikram,
    quote: "setup was quick and easy. after that, managing day-to-day tasks became much simpler.",
    badgeColor: "#C8E6C9",
  },
  // USA
  {
    name: "Michael Carter",
    role: "fitness studio owner",
    avatar: imgMichael,
    quote: "we were handling most things manually before. now it's all more streamlined and saves us time daily.",
    badgeColor: "#C8E6C9",
  },
  {
    name: "Ethan Walker",
    role: "event organiser",
    avatar: imgEthan,
    quote: "planning and managing everything used to take a lot of effort. now it feels more organised and manageable.",
    badgeColor: "#B3E5FC",
  },
  // Indonesia
  {
    name: "Putri Maheswari",
    role: "boutique owner",
    avatar: imgPutri,
    quote: "it's simple to use and doesn't take much time to figure out. i can manage things better now without depending on others.",
    badgeColor: "#F5CBA7",
  },
  {
    name: "Dewi Lestari",
    role: "home bakery",
    avatar: imgDewi,
    quote: "i like that it's not complicated. just helps keep things in order without adding extra work.",
    badgeColor: "#CDB7F6",
  },
  // Ghana
  {
    name: "Kwame Mensah",
    role: "cloud kitchen owner",
    avatar: imgKwame,
    quote: "earlier we didn't have much clarity on daily operations. now it's easier to track and stay on top of things.",
    badgeColor: "#F4C2C2",
  },
];

const CARD_WIDTH = 344; // card width + gap

export default function HearFromOurClients({ items = DEFAULT_TESTIMONIALS }) {
  const trackRef = useRef(null);
  const isPaused = useRef(false);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);

  // Auto-scroll loop using scrollLeft so manual scroll works alongside it
  const startScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const step = (timestamp) => {
      if (!isPaused.current) {
        const delta = lastTimeRef.current ? timestamp - lastTimeRef.current : 0;
        el.scrollLeft += delta * 0.2; // speed: px per ms factor
        lastTimeRef.current = timestamp;

        // Seamless loop: when first half scrolled past, jump back silently
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft -= el.scrollWidth / 2;
        }
      } else {
        lastTimeRef.current = null;
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    startScroll();
    return () => cancelAnimationFrame(rafRef.current);
  }, [startScroll]);

  const pause = () => { isPaused.current = true; };
  const resume = () => { isPaused.current = false; };

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    isPaused.current = true;
    el.scrollBy({ left: dir * CARD_WIDTH, behavior: "smooth" });
    // Resume auto-scroll after user stops interacting
    setTimeout(() => { isPaused.current = false; }, 1800);
  };

  // Drag-to-scroll
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const onMouseDown = (e) => {
    drag.current = { active: true, startX: e.pageX, scrollLeft: trackRef.current.scrollLeft };
    isPaused.current = true;
    trackRef.current.style.cursor = "grabbing";
  };

  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.pageX - drag.current.startX;
    trackRef.current.scrollLeft = drag.current.scrollLeft - dx;
  };

  const onMouseUp = () => {
    drag.current.active = false;
    trackRef.current.style.cursor = "grab";
    setTimeout(() => { isPaused.current = false; }, 1800);
  };

  return (
    <section className="hfc-wrap pt-20">
      <div className="hfc-container">
        <h2 className="hfc-title">
          hear from <span className="hfc-accent">our clients</span>
        </h2>
      </div>

      <div className="hfc-marquee-outer">
        {/* Left arrow */}
        <button className="hfc-arrow hfc-arrow--left" onClick={() => scrollBy(-1)} aria-label="Previous">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Scrollable track */}
        <div
          ref={trackRef}
          className="hfc-marquee-track"
          onMouseEnter={pause}
          onMouseLeave={() => { onMouseUp(); resume(); }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          {[...items, ...items].map((t, idx) => (
            <article key={idx} className="hfc-card" aria-hidden={idx >= items.length}>
              <header className="hfc-card-head">
                <span className="hfc-avatar" style={{ backgroundColor: t.badgeColor || "#EDE7F6" }}>
                  <img src={t.avatar} alt={t.name} className="hfc-avatar-img" />
                </span>
                <div className="hfc-id">
                  <div className="hfc-name">{t.name}</div>
                  <div className="hfc-role">{t.role}</div>
                </div>
              </header>
              <hr className="hfc-divider" />
              <p className="hfc-quote">{t.quote}</p>
            </article>
          ))}
        </div>

        {/* Right arrow */}
        <button className="hfc-arrow hfc-arrow--right" onClick={() => scrollBy(1)} aria-label="Next">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  );
}
